import { useDSSortStore } from "@/store/ds-listing-sort";
import { DSContractListing } from "@myth-finance/dualstake-ts-sdk";
import { useWallet } from "@txnlab/use-wallet";
import { useMemo } from "react";
import { useAlgoAcctInfo } from "./use-algo-account";
import { FullAprBreakdown } from "./use-apr-breakdown";
import { useFarmsAndAPRs } from "./use-farms-and-aprs";
import { useListings } from "./use-listings";

type Sorter = (
  userAssets?: UserAssets
) => (a: ListingWithApr, b: ListingWithApr) => 1 | 0 | -1;

export interface SortConfig {
  display: string;
  value: string;
  isAscending: boolean;
  sorter: Sorter;
}

type UserAssets = Map<bigint, bigint>;
function hasUserAsset(assetId: bigint, userAssets: UserAssets): boolean {
  if (!userAssets) return true;
  return userAssets.has(assetId) && userAssets.get(assetId)! > 0n;
}

function preSort(
  a: ListingWithApr,
  b: ListingWithApr,
  userAssets?: UserAssets
): -1 | undefined | 1 {
  if (!userAssets) return undefined;
  const hasA = hasUserAsset(a.lstId, userAssets);
  const hasB = hasUserAsset(b.lstId, userAssets);
  if ((hasA && hasB) || (!hasA && !hasB)) return undefined;
  if (hasA) return -1;
  if (hasB) return 1;
}

export const sortConfigs: SortConfig[] = [
  {
    display: "Highest TVL first",
    value: "tvl_d",
    isAscending: true,
    sorter:
      (userAssets?: UserAssets) => (a: ListingWithApr, b: ListingWithApr) =>
        preSort(a, b, userAssets) ?? (a.staked > b.staked ? -1 : 1),
  },
  {
    display: "Highest APR first",
    value: "apr_d",
    isAscending: true,
    sorter:
      (userAssets?: UserAssets) => (a: ListingWithApr, b: ListingWithApr) =>
        preSort(a, b, userAssets) ??
        (a.apr?.netBaseAprBps! + a.apr?.farmAprBps! >
        b.apr?.netBaseAprBps! + b.apr?.farmAprBps!
          ? -1
          : 1),
  },
  {
    display: "Newest first",
    value: "id_d",
    isAscending: true,
    sorter:
      (userAssets?: UserAssets) => (a: ListingWithApr, b: ListingWithApr) =>
        preSort(a, b, userAssets) ?? (a.appId > b.appId ? -1 : 1),
  },
  {
    display: "Lowest TVL first",
    value: "tvl_a",
    isAscending: false,
    sorter:
      (userAssets?: UserAssets) => (a: ListingWithApr, b: ListingWithApr) =>
        preSort(a, b, userAssets) ?? (a.staked < b.staked ? -1 : 1),
  },

  {
    display: "Lowest APR first",
    value: "apr_a",
    isAscending: false,
    sorter:
      (userAssets?: UserAssets) => (a: ListingWithApr, b: ListingWithApr) =>
        preSort(a, b, userAssets) ??
        (a.apr?.netBaseAprBps! + a.apr?.farmAprBps! <
        b.apr?.netBaseAprBps! + b.apr?.farmAprBps!
          ? -1
          : 1),
  },

  {
    display: "Oldest first",
    value: "id_a",
    isAscending: false,
    sorter:
      (userAssets?: UserAssets) => (a: ListingWithApr, b: ListingWithApr) =>
        preSort(a, b, userAssets) ?? (a.appId < b.appId ? -1 : 1),
  },
];

export type ListingWithApr = DSContractListing & { apr?: FullAprBreakdown };

export function useSortedListings(
  showWithFarms: boolean,
  showWithoutFarms: boolean
) {
  const { ownFirst, sortValue } = useDSSortStore();
  const selectedSort = useMemo(
    () => sortConfigs.find(({ value: v }) => v === sortValue) ?? sortConfigs[0],
    [sortValue]
  );

  const { activeAddress } = useWallet();
  const { data: listings } = useListings();
  const { data: farmsAndAprs } = useFarmsAndAPRs(
    listings ? [...listings.values()] : []
  );

  const { data: accountData } = useAlgoAcctInfo(activeAddress);

  const userAssets = useMemo(() => {
    if (accountData?.assets)
      return new Map(
        accountData.assets.map(({ assetId, amount }) => [
          BigInt(assetId),
          BigInt(amount),
        ])
      );
  }, [accountData?.assets]);

  const listingsWithApr: ListingWithApr[] = useMemo(() => {
    return listings?.size && farmsAndAprs?.size
      ? [...listings.values()]
          .filter(
            (listing: DSContractListing) =>
              (showWithFarms && !!farmsAndAprs.get(listing.appId)?.farm) ||
              (showWithoutFarms && !farmsAndAprs.get(listing.appId)?.farm)
          )
          .map((listing: DSContractListing) => ({
            ...listing,
            apr: farmsAndAprs.get(listing.appId)?.apr,
          }))
      : [];
  }, [listings, farmsAndAprs, showWithFarms, showWithoutFarms]);

  return useMemo(
    () =>
      !listingsWithApr?.length
        ? []
        : listingsWithApr.sort(
            selectedSort.sorter(ownFirst && userAssets ? userAssets : undefined)
          ),
    [selectedSort, listingsWithApr, ownFirst, accountData?.assets]
  );
}
